import type { LoadType } from './enums.js';
import { known, unknown, type Maybe } from './types.js';

/**
 * The load model.
 *
 * Converts the several ways resistance can be applied into one comparable
 * number — *effective load in grams* — so everything downstream (volume, PRs,
 * progression) has a single quantity to reason about.
 *
 * | load type         | effective load                  |
 * |-------------------|---------------------------------|
 * | external          | weight                          |
 * | bodyweight        | bodyweight                      |
 * | bodyweight_plus   | bodyweight + added              |
 * | assisted          | bodyweight − assistance         |
 * | none              | not applicable                  |
 *
 * The assisted case is why this works so cleanly: because assistance is
 * *subtracted*, dropping from 30 kg to 20 kg of help increases effective load
 * automatically. Nothing downstream needs to know that "less is better" here.
 */

export type LoadUnknownReason =
  | 'unknown_bodyweight'
  | 'missing_weight'
  | 'missing_assistance'
  | 'not_applicable';

export type EffectiveLoad = Maybe<number, LoadUnknownReason>;

/** The subset of a set that determines its load. */
export interface LoadInput {
  loadType: LoadType;
  weightG: number | null;
  bodyweightG: number | null;
  addedWeightG: number | null;
  assistWeightG: number | null;
}

/**
 * Effective load in grams.
 *
 * Returns an explicit "unknown" rather than a number whenever the inputs cannot
 * support one. **Unknown is never coerced to zero**: a bodyweight set logged
 * before any body weight was recorded is a perfectly valid set, and reporting
 * its load as 0 kg would quietly corrupt every total it feeds into.
 */
export function effectiveLoadG(input: LoadInput): EffectiveLoad {
  switch (input.loadType) {
    case 'external': {
      if (input.weightG === null) return unknown('missing_weight');
      return known(Math.max(0, input.weightG));
    }

    case 'bodyweight': {
      if (input.bodyweightG === null) return unknown('unknown_bodyweight');
      return known(Math.max(0, input.bodyweightG));
    }

    case 'bodyweight_plus': {
      if (input.bodyweightG === null) return unknown('unknown_bodyweight');
      return known(Math.max(0, input.bodyweightG + (input.addedWeightG ?? 0)));
    }

    case 'assisted': {
      if (input.assistWeightG === null) return unknown('missing_assistance');
      if (input.bodyweightG === null) return unknown('unknown_bodyweight');
      // Clamped at zero: assistance exceeding bodyweight would imply negative
      // resistance, which is not a meaningful contribution to volume.
      return known(Math.max(0, input.bodyweightG - input.assistWeightG));
    }

    case 'none':
      return unknown('not_applicable');
  }
}

/** Whether this load type contributes to strength volume at all. */
export function contributesToStrengthVolume(loadType: LoadType): boolean {
  return loadType !== 'none';
}

/**
 * Comparable performance value for an assisted exercise.
 *
 * Assistance is reported as a positive number where **lower is better**. When
 * bodyweight is unknown the effective load cannot be computed, but two assisted
 * sets can still be compared by assistance alone — which is what makes
 * "30 kg of help down to 20 kg" register as progress even before any bodyweight
 * has ever been recorded.
 */
export function assistanceG(input: LoadInput): EffectiveLoad {
  if (input.loadType !== 'assisted') return unknown('not_applicable');
  if (input.assistWeightG === null) return unknown('missing_assistance');
  return known(Math.max(0, input.assistWeightG));
}

/**
 * Whether `candidate` is a heavier effective load than `reference`.
 * Unknown values never win and never lose — they are simply not comparable.
 */
export function isHeavier(candidate: EffectiveLoad, reference: EffectiveLoad): boolean {
  return candidate.known && reference.known && candidate.value > reference.value;
}

/**
 * Whether `candidate` represents less assistance than `reference`, i.e. an
 * improvement on an assisted movement. The comparator is inverted here and
 * nowhere else.
 */
export function isLessAssistance(candidate: EffectiveLoad, reference: EffectiveLoad): boolean {
  return candidate.known && reference.known && candidate.value < reference.value;
}

export interface BodyweightResolution {
  bodyweightG: number | null;
  source: 'measured' | 'carried_forward' | 'backfilled' | 'unknown';
}

/**
 * Choose the bodyweight to record against a set.
 *
 * Deliberately conservative: when nothing is known it returns `null` with
 * source `unknown` rather than guessing. A set must always be loggable, and a
 * missing bodyweight is recoverable later by backfilling — an invented one is
 * not, because nothing marks it as wrong.
 */
export function resolveBodyweight(options: {
  /** Explicitly entered for this set. */
  explicitG?: number | null;
  /** Most recent recorded body measurement, if any. */
  latestMeasuredG?: number | null;
}): BodyweightResolution {
  const explicit = options.explicitG ?? null;
  if (explicit !== null) {
    return { bodyweightG: Math.max(0, explicit), source: 'measured' };
  }

  const latest = options.latestMeasuredG ?? null;
  if (latest !== null) {
    // Carried forward from the last measurement rather than measured today.
    // Recorded as such so statistics can be honest about their provenance.
    return { bodyweightG: Math.max(0, latest), source: 'carried_forward' };
  }

  return { bodyweightG: null, source: 'unknown' };
}
